import CheckIcon from "@mui/icons-material/Check";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Link,
  SvgIcon,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { ArrowRight as ArrowRightIcon, Edit as EditIcon } from "react-feather";
import { Link as RouterLink } from "react-router-dom";
import { useImpersonate } from "src/hooks/useImpersonate";
import { makeStyles } from "tss-react/mui";
import { brand } from "../core/Theme";
import { USER_BADGES } from "../helpers/const";
import { adminImageUrl } from "../helpers/utils";
import { Spacing } from "../types/enum";
import { type UserModel, type ZoneLabel } from "../types/graphql";
import Label from "./Label";
import ZoneChip from "./ZoneChip";

/**
 * Types
 */
/**
 * The per-member facts the list shows that aren't columns on the user herself.
 *
 * Declared here rather than taken from the generated types because the row is
 * what decides which of them it shows.
 */
export interface MemberStats {
  zone?: ZoneLabel | null;
  eventsAttended?: number | null;
  /** Where she stands with us — Member, Cancelling, Paused, Cancelled, Trial. */
  status?: string | null;
  /** When the team last sent her a chat message, by any tool. */
  lastMessagedAt?: string | number | Date | null;
  plan?: string | null;
  lastOnlineAt?: string | number | Date | null;
  faderLevel?: string | null;
  engagementLevel?: string | null;
  commitmentLevel?: string | null;
  loginsRecent?: number | null;
  tenure?: string | null;
}

interface Props {
  user: UserModel;
  selected?: string[];
  allSelected?: boolean;
  onSelectOne?: (id: string) => void;
  /**
   * The worked-out facts about her — zone, levels, plan and the rest. Fetched
   * for the whole page at once, so it arrives after the row does.
   */
  stats?: MemberStats | null;
  /** Active bio keyword, so the row can show the matching words. */
  storyKeyword?: string;
  recoveryDaysRemaining?: number;
}

/** Colour by what the status means, not by rank: leaving reads as a warning. */
const STATUS_COLOUR: Record<string, "success" | "warning" | "error" | "info" | "default"> = {
  Member: "success",
  Trial: "info",
  Cancelling: "warning",
  Paused: "warning",
  Cancelled: "default",
};

/**
 * The words around a bio match, so you can see WHY she came back in the results
 * rather than having to open her profile. Empty unless a bio keyword is active.
 */
const storySnippet = (story?: string | null, keyword?: string): string => {
  if (!story || !keyword) {
    return "";
  }
  const at = story.toLowerCase().indexOf(keyword.toLowerCase());
  if (at === -1) {
    return "";
  }
  const from = Math.max(0, at - 40);
  return story.slice(from, at + keyword.length + 40).trim();
};

/**
 * The Date scalar arrives as an epoch number, but a numeric *string* would make
 * `new Date` return Invalid Date, so coerce that case before parsing.
 */
const daysSince = (when: string | number | Date): number => {
  const value = typeof when === "string" && /^\d+$/.test(when) ? Number(when) : when;
  return Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000);
};

/**
 * Styles
 */
const useStyles = makeStyles()({
  title: {
    maxWidth: "240px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  verifiedIcon: {
    color: brand.green,
  },
});

/**
 * The list is wider than a screen now, so it scrolls sideways — and the two
 * columns that say *who this row is* stay put while it does. Without them you
 * scroll right to read a level and lose track of whose level it is.
 *
 * Shared with the header, which pins the same two columns at a higher layer so
 * it survives both directions of scroll at once.
 */
export const STICKY_CHECKBOX = {
  position: "sticky",
  left: 0,
  zIndex: 2,
  bgcolor: "background.paper",
} as const;

export const STICKY_NAME = {
  position: "sticky",
  left: 58,
  zIndex: 2,
  bgcolor: "background.paper",
} as const;

const Muted: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" color="textSecondary">
    {children || "—"}
  </Typography>
);

const UsersPageUserDetails: React.FC<Props> = ({
  user,
  selected,
  onSelectOne,
  allSelected,
  stats,
  storyKeyword,
  recoveryDaysRemaining,
}: Props) => {
  const { classes } = useStyles();
  const impersonateMutation = useImpersonate(user.id);
  const snippet = storySnippet(user?.story, storyKeyword);

  const isSelected = selected?.includes(user.id);

  const renderers = {
    location: () => {
      return (
        <>
          {user?.city}, {user?.country}
        </>
      );
    },
  };

  return (
    <TableRow key={user?.id} hover>
      <TableCell padding="checkbox" sx={STICKY_CHECKBOX}>
        <Checkbox
          checked={isSelected || allSelected}
          onChange={() => {
            onSelectOne?.(user.id);
          }}
        />
      </TableCell>

      <TableCell sx={STICKY_NAME}>
        <Box display="flex" alignItems="center">
          <Avatar src={adminImageUrl(user?.avatarUrl, { width: 96 })} />
          <Box ml={Spacing.sm}>
            <Box display="flex">
              <Link color="inherit" component={RouterLink} to={`/users/${user.id}`}>
                <Typography variant="body2" className={classes.title}>
                  {user?.name}
                </Typography>

                {user?.isVerified ? (
                  <Box ml={Spacing.sm}>
                    <CheckIcon className={classes.verifiedIcon} />
                  </Box>
                ) : null}
              </Link>
            </Box>
            {snippet ? (
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                sx={{ maxWidth: 320 }}
              >
                “…{snippet}…”
              </Typography>
            ) : null}
          </Box>
        </Box>
      </TableCell>

      <TableCell>
        {stats?.status ? (
          <Chip
            size="small"
            label={stats.status}
            color={STATUS_COLOUR[stats.status] ?? "default"}
          />
        ) : (
          <Typography variant="caption" color="textSecondary">
            —
          </Typography>
        )}
      </TableCell>

      <TableCell>
        <Box ml={Spacing.sm}>
          {user?.badge ? USER_BADGES.get(user?.badge) : format(user?.createdAt, "LLL yyyy")}
        </Box>
      </TableCell>

      <TableCell>
        <Label variant="info">{user?.email}</Label>
      </TableCell>

      <TableCell>
        {user?.city ? (
          <Label variant="success">{renderers.location()}</Label>
        ) : (
          <Label variant="error">N/A</Label>
        )}
      </TableCell>

      <TableCell>
        <Typography variant="body2">{format(new Date(user?.createdAt), "yyyy-MM-dd")}</Typography>
      </TableCell>

      <TableCell>
        <ZoneChip zone={stats?.zone} />
      </TableCell>

      <TableCell>
        <Muted>{stats?.tenure}</Muted>
      </TableCell>

      <TableCell>
        <Muted>{stats?.plan}</Muted>
      </TableCell>

      <TableCell>
        <Muted>{user?.cambridgeEnglishLevel}</Muted>
      </TableCell>

      <TableCell>
        <Muted>{stats?.faderLevel}</Muted>
      </TableCell>

      <TableCell>
        <Muted>{stats?.engagementLevel}</Muted>
      </TableCell>

      <TableCell>
        <Muted>{stats?.commitmentLevel}</Muted>
      </TableCell>

      <TableCell align="center">
        <Typography variant="body2" fontWeight={stats?.loginsRecent ? 600 : 400}>
          {stats?.loginsRecent ?? "—"}
        </Typography>
      </TableCell>

      <TableCell>
        {stats?.lastOnlineAt ? (
          <Typography variant="body2" color="textSecondary">
            {daysSince(stats.lastOnlineAt) === 0
              ? "today"
              : `${daysSince(stats.lastOnlineAt)}d ago`}
          </Typography>
        ) : (
          <Muted />
        )}
      </TableCell>

      <TableCell align="center">
        <Typography variant="body2" fontWeight={stats?.eventsAttended ? 600 : 400}>
          {stats?.eventsAttended ?? "—"}
        </Typography>
      </TableCell>

      {recoveryDaysRemaining !== undefined ? (
        <TableCell>
          <Typography variant="body2">{recoveryDaysRemaining}</Typography>
        </TableCell>
      ) : null}

      <TableCell>
        {stats?.lastMessagedAt ? (
          <Typography
            variant="caption"
            // Recently contacted — worth seeing before you message her again.
            color={daysSince(stats.lastMessagedAt) <= 7 ? "error" : "textSecondary"}
            fontWeight={daysSince(stats.lastMessagedAt) <= 7 ? 600 : 400}
          >
            {daysSince(stats.lastMessagedAt) === 0
              ? "today"
              : `${daysSince(stats.lastMessagedAt)}d ago`}
          </Typography>
        ) : (
          <Typography variant="caption" color="textSecondary">
            never
          </Typography>
        )}
      </TableCell>

      <TableCell align="right">
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            impersonateMutation.mutate();
          }}
        >
          Impersonate
        </Button>
        <Link component={RouterLink} to={`/users/${user.id}/edit`}>
          <IconButton size="large">
            <SvgIcon fontSize="small">
              <EditIcon />
            </SvgIcon>
          </IconButton>
        </Link>
        <Link color="inherit" component={RouterLink} to={`/users/${user.id}`}>
          <IconButton size="large">
            <SvgIcon fontSize="small">
              <ArrowRightIcon />
            </SvgIcon>
          </IconButton>
        </Link>
      </TableCell>
    </TableRow>
  );
};

export default UsersPageUserDetails;
